import React from "react";
import { motion } from "framer-motion";
import {
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Clock3,
  Activity,
  Server,
  Video,
  CalendarCheck,
  FileText,
  Pill,
  CreditCard,
  ShieldCheck,
  RefreshCw,
  Bell,
  ArrowRight,
  MessageSquare,
} from "lucide-react";
import Navbar from "../../../DashBoardComponents/Navbar";

const SystemStatus = () => {
  const services = [
    {
      icon: Activity,
      name: "Doxzon Platform",
      description: "Core Doxzon platform services",
      status: "Operational",
    },
    {
      icon: CalendarCheck,
      name: "Appointments",
      description: "Appointment booking and management",
      status: "Operational",
    },
    {
      icon: Video,
      name: "Virtual Care",
      description: "Virtual consultation services",
      status: "Operational",
    },
    {
      icon: FileText,
      name: "Health Records",
      description: "Digital health records and documents",
      status: "Operational",
    },
    {
      icon: Pill,
      name: "Prescriptions",
      description: "Prescription-related services",
      status: "Operational",
    },
    {
      icon: CreditCard,
      name: "Payments",
      description: "Billing and payment services",
      status: "Operational",
    },
  ];

  const uptime = [
    {
      label: "Today",
      value: "100%",
    },
    {
      label: "Last 7 days",
      value: "100%",
    },
    {
      label: "Last 30 days",
      value: "99.99%",
    },
  ];

  const updates = [
    {
      date: "System Update",
      title: "Regular platform maintenance",
      description:
        "Scheduled maintenance and platform improvements help keep Doxzon services reliable.",
    },
    {
      date: "Platform",
      title: "Performance improvements",
      description:
        "Ongoing improvements are being made across the Doxzon platform experience.",
    },
  ];

  return (
    <>
      <Navbar />

      <main className="bg-white text-slate-900 overflow-hidden">

        {/* =====================================================
            HERO
        ====================================================== */}
        <section className="relative overflow-hidden bg-[#f4fbfc]">

          <div className="absolute -top-40 -right-40 w-[650px] h-[650px] rounded-full bg-teal-200/25 blur-3xl" />

          <div className="absolute -bottom-40 -left-40 w-[550px] h-[550px] rounded-full bg-cyan-200/25 blur-3xl" />

          <div
            className="absolute inset-0 opacity-[0.025]"
            style={{
              backgroundImage:
                "linear-gradient(#0f172a 1px, transparent 1px), linear-gradient(90deg, #0f172a 1px, transparent 1px)",
              backgroundSize: "48px 48px",
            }}
          />

          <div className="relative max-w-5xl mx-auto px-6 pt-20 pb-24 lg:pt-28 lg:pb-32 text-center">

            <motion.div
              initial={{ opacity: 0, y: 25 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
            >

              <div className="inline-flex items-center gap-2 rounded-full bg-white border border-teal-100 px-4 py-2 shadow-sm">

                <span className="w-2 h-2 rounded-full bg-teal-500 animate-pulse" />

                <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-teal-700">
                  Doxzon System Status
                </span>

              </div>

              <h1 className="font-sora text-5xl md:text-6xl lg:text-[62px] font-medium tracking-[-0.055em] leading-[1.03] mt-7">

                Everything is
                <span className="block text-teal-500 mt-2">
                  running smoothly.
                </span>

              </h1>

              <p className="text-slate-600 text-lg md:text-xl leading-8 mt-6 max-w-2xl mx-auto">
                Check the current status of Doxzon services, platform
                availability and recent system updates.
              </p>

              {/* GLOBAL STATUS */}
              <div className="max-w-xl mx-auto mt-10 rounded-3xl bg-white border border-slate-100 shadow-[0_20px_60px_rgba(15,23,42,0.08)] p-6">

                <div className="flex items-center justify-center gap-3">

                  <div className="w-12 h-12 rounded-full bg-teal-50 grid place-items-center">

                    <CheckCircle2
                      size={26}
                      className="text-teal-600"
                    />

                  </div>

                  <div className="text-left">

                    <p className="font-sora text-lg font-semibold">
                      All Systems Operational
                    </p>

                    <p className="text-xs text-slate-400 mt-1">
                      No active incidents reported
                    </p>

                  </div>

                </div>

                <div className="flex items-center justify-center gap-2 text-xs text-slate-400 mt-5">

                  <RefreshCw size={13} />

                  Status updated recently

                </div>

              </div>

            </motion.div>

          </div>

        </section>

        {/* =====================================================
            SERVICES
        ====================================================== */}
        <section className="py-24 md:py-28">

          <div className="max-w-6xl mx-auto px-6">

            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">

              <div>

                <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-600">
                  Service Status
                </span>

                <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                  Doxzon services
                  <span className="text-teal-500">
                    {" "}at a glance.
                  </span>

                </h2>

              </div>

              <div className="inline-flex items-center gap-2 text-sm text-slate-500">

                <span className="w-2 h-2 rounded-full bg-teal-500" />

                Operational

              </div>

            </div>

            <div className="mt-12 rounded-3xl border border-slate-100 overflow-hidden">

              {services.map((service, index) => {

                const Icon = service.icon;

                return (
                  <motion.div
                    key={service.name}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    className={`flex items-center gap-4 md:gap-6 p-5 md:p-6 bg-white hover:bg-slate-50 transition ${
                      index !== services.length - 1
                        ? "border-b border-slate-100"
                        : ""
                    }`}
                  >

                    <div className="w-11 h-11 md:w-12 md:h-12 rounded-xl bg-teal-50 grid place-items-center shrink-0">

                      <Icon
                        size={20}
                        className="text-teal-600"
                      />

                    </div>

                    <div className="flex-1 min-w-0">

                      <h3 className="font-semibold text-sm md:text-base">
                        {service.name}
                      </h3>

                      <p className="text-xs md:text-sm text-slate-400 mt-1">
                        {service.description}
                      </p>

                    </div>

                    <div className="flex items-center gap-2 shrink-0">

                      <span className="w-2 h-2 rounded-full bg-teal-500" />

                      <span className="hidden sm:inline text-sm font-medium text-teal-700">
                        {service.status}
                      </span>

                    </div>

                  </motion.div>
                );

              })}

            </div>

          </div>

        </section>

        {/* =====================================================
            UPTIME
        ====================================================== */}
        <section className="py-24 md:py-28 bg-[#f6fbfc]">

          <div className="max-w-6xl mx-auto px-6">

            <div className="text-center max-w-2xl mx-auto">

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-600">
                Availability
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                Reliability you can
                <span className="block text-teal-500">
                  keep an eye on.
                </span>

              </h2>

              <p className="text-slate-600 leading-7 mt-5">
                Review recent service availability and platform performance.
              </p>

            </div>

            <div className="grid md:grid-cols-3 gap-5 mt-12">

              {uptime.map((item, index) => (

                <motion.div
                  key={item.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.07 }}
                  className="rounded-3xl bg-white border border-slate-100 p-8 text-center"
                >

                  <div className="w-12 h-12 mx-auto rounded-2xl bg-teal-50 grid place-items-center">

                    <Activity
                      size={21}
                      className="text-teal-600"
                    />

                  </div>

                  <p className="text-slate-500 text-sm mt-5">
                    {item.label}
                  </p>

                  <p className="font-sora text-4xl font-semibold text-slate-950 mt-2">
                    {item.value}
                  </p>

                  <div className="flex justify-center items-center gap-2 text-xs text-teal-600 mt-4">

                    <CheckCircle2 size={13} />

                    Service availability

                  </div>

                </motion.div>

              ))}

            </div>

          </div>

        </section>

        {/* =====================================================
            STATUS HISTORY
        ====================================================== */}
        <section className="py-24 md:py-28">

          <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-14">

            {/* HISTORY */}
            <div>

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-600">
                Status History
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                Recent
                <span className="text-teal-500">
                  {" "}updates.
                </span>

              </h2>

              <div className="mt-10 space-y-4">

                {updates.map((item, index) => (

                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, x: -15 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.08 }}
                    className="rounded-2xl border border-slate-100 bg-slate-50 p-5 hover:bg-white hover:shadow-lg hover:border-teal-100 transition"
                  >

                    <div className="flex gap-4">

                      <div className="w-10 h-10 rounded-xl bg-white grid place-items-center shrink-0">

                        <Clock3
                          size={18}
                          className="text-teal-600"
                        />

                      </div>

                      <div>

                        <span className="text-[10px] uppercase tracking-[0.15em] font-bold text-teal-600">
                          {item.date}
                        </span>

                        <h3 className="font-semibold mt-2">
                          {item.title}
                        </h3>

                        <p className="text-slate-500 text-sm leading-6 mt-2">
                          {item.description}
                        </p>

                      </div>

                    </div>

                  </motion.div>

                ))}

              </div>

            </div>

            {/* INCIDENT CARD */}
            <div>

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-600">
                Current Incidents
              </span>

              <div className="mt-4 rounded-3xl border border-slate-100 bg-white shadow-sm p-8">

                <div className="w-14 h-14 rounded-2xl bg-teal-50 grid place-items-center">

                  <CheckCircle2
                    size={28}
                    className="text-teal-600"
                  />

                </div>

                <h3 className="font-sora text-2xl font-semibold mt-7">
                  No active incidents
                </h3>

                <p className="text-slate-500 leading-7 mt-3">
                  There are currently no reported incidents affecting Doxzon
                  services.
                </p>

                <div className="mt-7 pt-6 border-t border-slate-100">

                  <div className="flex items-center justify-between text-sm">

                    <span className="text-slate-400">
                      Overall status
                    </span>

                    <span className="inline-flex items-center gap-2 text-teal-600 font-semibold">

                      <span className="w-2 h-2 rounded-full bg-teal-500" />

                      Operational

                    </span>

                  </div>

                </div>

              </div>

            </div>

          </div>

        </section>

        {/* =====================================================
            INCIDENT TYPES
        ====================================================== */}
        <section className="py-24 bg-slate-950 text-white">

          <div className="max-w-6xl mx-auto px-6">

            <div className="text-center max-w-2xl mx-auto">

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-400">
                Status Indicators
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                What each status
                <span className="block text-teal-400">
                  means.
                </span>

              </h2>

            </div>

            <div className="grid md:grid-cols-3 gap-5 mt-12">

              {/* OPERATIONAL */}
              <div className="rounded-3xl bg-white/[0.05] border border-white/10 p-7">

                <div className="w-12 h-12 rounded-2xl bg-teal-500/10 grid place-items-center">

                  <CheckCircle2
                    size={23}
                    className="text-teal-400"
                  />

                </div>

                <h3 className="font-sora text-lg font-semibold mt-6">
                  Operational
                </h3>

                <p className="text-slate-500 text-sm leading-7 mt-3">
                  The service is operating normally without any known
                  disruption.
                </p>

              </div>

              {/* DEGRADED */}
              <div className="rounded-3xl bg-white/[0.05] border border-white/10 p-7">

                <div className="w-12 h-12 rounded-2xl bg-amber-500/10 grid place-items-center">

                  <AlertTriangle
                    size={23}
                    className="text-amber-400"
                  />

                </div>

                <h3 className="font-sora text-lg font-semibold mt-6">
                  Degraded Performance
                </h3>

                <p className="text-slate-500 text-sm leading-7 mt-3">
                  A service may be experiencing slower performance or limited
                  functionality.
                </p>

              </div>

              {/* OUTAGE */}
              <div className="rounded-3xl bg-white/[0.05] border border-white/10 p-7">

                <div className="w-12 h-12 rounded-2xl bg-red-500/10 grid place-items-center">

                  <XCircle
                    size={23}
                    className="text-red-400"
                  />

                </div>

                <h3 className="font-sora text-lg font-semibold mt-6">
                  Service Disruption
                </h3>

                <p className="text-slate-500 text-sm leading-7 mt-3">
                  A service is currently unavailable or significantly
                  affected.
                </p>

              </div>

            </div>

          </div>

        </section>

        {/* =====================================================
            SUPPORT CTA
        ====================================================== */}
        <section className="px-6 py-24">

          <div className="relative overflow-hidden max-w-7xl mx-auto rounded-[32px] bg-[#f4fbfc] border border-teal-50 px-8 py-16 md:px-16">

            <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-teal-200/30 blur-3xl" />

            <div className="absolute -bottom-28 -left-20 w-72 h-72 rounded-full bg-cyan-200/30 blur-3xl" />

            <div className="relative grid lg:grid-cols-2 gap-10 items-center">

              <div>

                <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-600">
                  Need Assistance?
                </span>

                <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                  Something doesn't
                  <span className="block text-teal-500">
                    look right?
                  </span>

                </h2>

                <p className="text-slate-600 leading-7 mt-5 max-w-xl">
                  If you're experiencing an issue that isn't reflected here,
                  contact the Doxzon support team for assistance.
                </p>

              </div>

              <div className="flex lg:justify-end">

                <button className="inline-flex items-center gap-2 rounded-xl bg-slate-950 text-white px-7 h-14 font-semibold hover:bg-teal-500 transition">

                  <MessageSquare size={18} />

                  Contact Support

                  <ArrowRight size={17} />

                </button>

              </div>

            </div>

          </div>

        </section>

      </main>
    </>
  );
};

export default SystemStatus;